/**
 * Creator Applications API Client
 *
 * API Endpoints:
 * - POST /creator-applications               → submit a new application
 * - GET  /creator-applications/me            → get the authenticated user's latest application + channel state
 *
 * Handler availability is served by the existing endpoint, see
 * handler.client.ts (expanded on 2026-10-20 to cover channels +
 * pending applications on top of users).
 *
 * BE contract: tabootv_bo/documentation/api-creator-applications.md
 */

import type {
  CreatorApplicationMeResponse,
  CreatorApplicationSubmitPayload,
  CreatorApplicationSubmitResponse,
} from '@/types/creator-application';
import { apiClient, type RequestConfig } from './base-client';

export const creatorApplicationsClient = {
  /**
   * Submit a new creator application. Requires auth — the account
   * exists at submission time (freshly registered via the same
   * /become-creator flow, or already signed-in).
   *
   * Error responses the UI should expect:
   * - 409 "You already have a creator channel."
   * - 409 "You already have a pending creator application."
   * - 422 with field errors from Laravel validator
   * - 429 when the per-IP creator-application throttle trips
   *
   * Sent as multipart/form-data (BE `0a4d59b`): `dp` + `banner` are files,
   * the rest keep their JSON names.
   */
  submit: async (
    payload: CreatorApplicationSubmitPayload
  ): Promise<CreatorApplicationSubmitResponse> => {
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined) formData.append(key, value);
    }
    return apiClient.post<CreatorApplicationSubmitResponse>('/creator-applications', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Two images of up to 10 MB each: the 30s default fails ~20 MB on a slow uplink.
      timeout: 120_000,
    });
  },

  /**
   * Fetch the authenticated user's current application state. Drives
   * which /become-creator screen renders:
   * - `is_creator: true`  → bounce to studio
   * - `application.status: 'pending'` → "we're reviewing it" screen
   * - everything else     → open the form
   */
  getMine: async (
    config?: Pick<RequestConfig, 'signal'>
  ): Promise<CreatorApplicationMeResponse> => {
    return apiClient.get<CreatorApplicationMeResponse>('/creator-applications/me', config);
  },
};
